import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  // Hydrate user from localStorage cache instantly to avoid login→landing 5-second blank.
  const cached = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
  const [user, setUser] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try { localStorage.setItem("user", JSON.stringify(data)); } catch { /* noop */ }
    } catch {
      // Token invalid / expired — wipe stale session so the app doesn't stay
      // stuck with a dead token that makes every subsequent call return 401.
      setUser(false);
      try {
        localStorage.removeItem("user");
        localStorage.removeItem("hf_token");
      } catch { /* noop */ }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("hf_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("user", JSON.stringify(data.user)); } catch {}
    return data.user;
  };

  const setSession = (data) => {
    if (data.token) localStorage.setItem("hf_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("user", JSON.stringify(data.user)); } catch {}
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("hf_token", data.token);
    setUser(data.user);
    try { localStorage.setItem("user", JSON.stringify(data.user)); } catch {}
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("hf_token");
    localStorage.removeItem("user");
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, setUser, setSession }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
